import { useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';

import { config } from '../../config.js';
import { useWorkspaceContext } from '../../context/WorkspaceContext.js';
import { useAuth } from '../../hooks/useAuth/useAuth.js';
import { isValidYyyyMmDd } from '../../hooks/useEvalRuns/useEvalRuns.js';
import { useWorkspaces } from '../../hooks/useWorkspaces/useWorkspaces.js';

import { useStyles } from './TrendsPage.style.js';

export type TrendsPageProps = Record<string, never>;

const IFRAME_TITLE = 'Teams Pulse — Eval Trends';

const AISTUDIO_NAVIGATE_MESSAGE_TYPE = 'aistudio:navigate';
const TEAMSPULSE_EXTERNAL_NAV_MESSAGE_TYPE = 'teamspulse:externalNavigate';

/**
 * sessionStorage key template kept only to purge any values a previous
 * revision of this handler wrote. Earlier iterations of the drill handler
 * stashed a "sticky task" so a subsequent drill from a metric-only tile
 * could fall back to it, but that always cached the Pulse dashboard's ADX
 * `TaskName` value (e.g. `classify-mode-and-signals`), which AI Studio's
 * /eval-runs API cannot match (it filters on `displayName`). The current
 * flow relies solely on the tile-emitted `taskName`, so we actively remove
 * any leftover value on every drill to prevent stale reads polluting the
 * `?search=` param.
 */
const STICKY_TASK_STORAGE_KEY = (workspace: string) =>
  `aistudio:trends:sticky-task:${workspace}`;

/**
 * Payload contract for cross-origin drill-down from a Teams Pulse dashboard
 * tile into AI Studio.
 *
 * Two wire formats are accepted:
 *   1. `teamspulse:externalNavigate` — the real Pulse contract. Tile authors call
 *      `TeamsPulse.externalNavigate({ target, runId?, taskName?, runDate?, params? })`;
 *      the Pulse dashboard shell relays a validated copy up to us here.
 *   2. `aistudio:navigate` — a legacy AI-Studio-only shape kept alive for
 *      the local dev drill-down tester in the Pulse repo. Same fields,
 *      different discriminator.
 *
 * Both are validated for origin (must be the configured Pulse origin) and
 * for shape before we route the shell.
 *
 * Fields:
 *   - `type`: one of the two literals above so unrelated postMessages
 *     (Vite HMR, MSAL, browser extensions, Pulse's intra-dashboard
 *     `teamspulse:navigate` tab-switch messages) are ignored.
 *   - `target`: which AI Studio surface to open. Adding a new target here
 *     is the only place a new drill-down needs to be registered.
 *   - `runId` / `taskName` / `runDate`: required by their respective
 *     targets. All optional-string fields on this shape are validated as
 *     strings by the guard so a stray non-string value from a trusted
 *     origin can't reach `encodeURIComponent` and throw.
 *
 * Note: cross-workspace drill-down is intentionally not supported. Both
 * `EvalRunsPage` and `EvalReportPage` re-derive their workspace from the
 * `WorkspaceContext.activeName` on mount, so passing a different
 * `workspaceName` here would only cause a transient URL that the
 * destination page immediately redirects away from. Drills always land in
 * the user's currently active workspace.
 */
interface AistudioNavigatePayload {
  type:
    | typeof AISTUDIO_NAVIGATE_MESSAGE_TYPE
    | typeof TEAMSPULSE_EXTERNAL_NAV_MESSAGE_TYPE;
  target: 'evalRunReport' | 'evalRunsList';
  runId?: string;
  taskName?: string;
  runDate?: string;
}

function isAistudioNavigatePayload(
  value: unknown,
): value is AistudioNavigatePayload {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const candidate = value as {
    type?: unknown;
    target?: unknown;
    runId?: unknown;
    taskName?: unknown;
    runDate?: unknown;
  };
  const typeOk =
    candidate.type === AISTUDIO_NAVIGATE_MESSAGE_TYPE ||
    candidate.type === TEAMSPULSE_EXTERNAL_NAV_MESSAGE_TYPE;
  const targetOk =
    candidate.target === 'evalRunReport' || candidate.target === 'evalRunsList';
  const isOptionalString = (v: unknown) =>
    v === undefined || typeof v === 'string';
  return (
    typeOk &&
    targetOk &&
    isOptionalString(candidate.runId) &&
    isOptionalString(candidate.taskName) &&
    isOptionalString(candidate.runDate)
  );
}

// Calendar-date validation is shared with `useEvalRuns` — imported below —
// so the runDate we forward as `?date=` uses exactly the same acceptance
// rule as the hook that consumes it.

/**
 * Trends surface for AI Studio. Renders a Teams Pulse dashboard in an iframe
 * so users get the full authoring/customisation model from Teams Pulse without
 * us reimplementing charts / filters / templates here.
 *
 * The active AI Studio workspace is projected into the iframe URL as the
 * dashboard's workspace filter (parameter name from `config.teamsPulseWorkspaceParam`).
 * Every other dashboard filter (time range, agents, CI, etc.) is owned by the
 * Teams Pulse dashboard itself. The active workspace's dashboard ID overrides
 * the environment default when configured. The iframe re-mounts (via `key`
 * on the URL) when the workspace or dashboard changes so queries rerun.
 *
 * Drill-down from Pulse tiles into AI Studio uses cross-origin `postMessage`
 * with the `aistudio:navigate` / `teamspulse:externalNavigate` protocol above. The
 * Pulse dashboard tile fires the message when a user clicks (e.g. a chart
 * point for a specific run, or a task row in the Tasks and agents table);
 * the handler here validates the origin against `config.teamsPulseBaseUrl`
 * and routes the shell to either the eval run report
 * (`.../evaluation/evaluation-runs/:id/report`) or a task/date-filtered
 * eval runs list (`.../eval-runs?search=<task>&date=<yyyy-mm-dd>`).
 *
 * Gating: enabled for all AI Studio users.
 *
 * Follow-ups tracked separately:
 *   - Teams Pulse CSP `frame-ancestors` must allowlist AI Studio Front Door
 *     origins (companion PR in teams-pulse).
 *   - Teams Pulse needs a chromeless embed mode (`?embed=1`) so the frame
 *     hides its own top nav/sidebar. Until then the frame shows Teams Pulse
 *     chrome.
 *   - Teams Pulse `?lockFilters=<name>` support so the workspace filter is
 *     disabled inside the frame.
 *   - Teams Pulse dashboard tiles need per-tile click actions configured to
 *     emit the `aistudio:navigate` message (companion PR in teams-pulse).
 */
export const TrendsPage = (_props: TrendsPageProps) => {
  const styles = useStyles();
  const { activeName } = useWorkspaceContext();
  const { activeWorkspace } = useWorkspaces();
  const { account } = useAuth();
  const navigate = useNavigate();
  const { workspaceName: workspaceNameParam } = useParams<{
    workspaceName: string;
  }>();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (activeName && workspaceNameParam && activeName !== workspaceNameParam) {
      void navigate(`/workspaces/${encodeURIComponent(activeName)}/trends`, {
        replace: true,
      });
    }
  }, [activeName, workspaceNameParam, navigate]);

  // Dev-only drill-down test params. When AI Studio is served by the Vite
  // dev server and the Trends URL carries `?_devDrillRunId=<id>` or
  // `?_devDrillTask=<name>`, forward those params into the iframe so the
  // Teams Pulse dev-only drill-down tester renders and lets us exercise
  // the `aistudio:navigate` handler without waiting on real tile authoring.
  const devDrillRunId = import.meta.env.DEV
    ? (searchParams.get('_devDrillRunId') ?? undefined)
    : undefined;
  const devDrillTask = import.meta.env.DEV
    ? (searchParams.get('_devDrillTask') ?? undefined)
    : undefined;
  const devDrillDate = import.meta.env.DEV
    ? (searchParams.get('_devDrillDate') ?? undefined)
    : undefined;

  const src = useMemo(() => {
    const base = config.teamsPulseBaseUrl.replace(/\/$/, '');
    const url = new URL(
      `${base}/embed/dashboards/${encodeURIComponent(activeWorkspace?.teamsPulseDashboardId ?? config.teamsPulseDashboardId)}`,
    );
    // Restore the last Pulse tab the user was on when hitting Back /
    // Forward. `_pulseTab` is written by the `teamspulse:navigate` handler
    // below whenever Pulse's own tab-switcher fires; on a fresh load
    // (or no Pulse-state URL params yet) we default to summary.
    const restoreTab = searchParams.get('_pulseTab') ?? 'summary';
    url.searchParams.set('tab', restoreTab);
    // `_pulseParams` carries a URL-encoded query-string blob of the
    // dashboard filter state the Pulse shell last reported (e.g.
    // `taskName=evaluate-quorum-agent&graderName=...`). Re-inflate it so
    // the iframe boots with those filters applied.
    const restoreParams = searchParams.get('_pulseParams');
    if (restoreParams) {
      try {
        const inner = new URLSearchParams(restoreParams);
        inner.forEach((v, k) => {
          // Never let restored params override the sub-frame's `tab`,
          // workspace, or lockFilters — those are host-managed.
          if (k === 'tab' || k === 'lockFilters') return;
          if (k === config.teamsPulseWorkspaceParam) return;
          url.searchParams.set(k, v);
        });
      } catch {
        // Bad param blob — ignore and boot with default filters.
      }
    }
    if (activeName) {
      url.searchParams.set(config.teamsPulseWorkspaceParam, activeName);
    }
    url.searchParams.set('lockFilters', config.teamsPulseWorkspaceParam);
    // Pass the signed-in AI Studio user's UPN as a login hint so the
    // Pulse embed can prefill the sign-in CTA with the right account
    // when no cached MSAL session exists in the pulse.* origin.
    if (account?.username) {
      url.searchParams.set('loginHint', account.username);
    }
    if (devDrillRunId) {
      url.searchParams.set('_devDrillRunId', devDrillRunId);
    }
    if (devDrillTask) {
      url.searchParams.set('_devDrillTask', devDrillTask);
    }
    if (devDrillDate) {
      url.searchParams.set('_devDrillDate', devDrillDate);
    }
    return url.toString();
    // We intentionally do NOT depend on `searchParams` here as a whole —
    // that would re-mount the iframe on every unrelated URL update. We
    // only care about the Pulse-state params, and we snapshot them
    // whenever the workspace or the account changes (typical remount
    // triggers). The `teamspulse:navigate` handler below uses
    // { replace: true } for tab-mirror writes so the URL updates without
    // triggering iframe re-mount either — the src memo below re-reads
    // `_pulseTab` / `_pulseParams` only on the next real navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeName,
    activeWorkspace?.teamsPulseDashboardId,
    account?.username,
    devDrillRunId,
    devDrillTask,
    devDrillDate,
  ]);

  // Origin of the Pulse frame — precomputed once per config so the message
  // listener can reject anything that isn't from the embedded dashboard.
  const pulseOrigin = useMemo(() => {
    try {
      return new URL(config.teamsPulseBaseUrl).origin;
    } catch {
      return '';
    }
  }, []);

  useEffect(() => {
    if (!pulseOrigin) {
      return;
    }
    const handler = (event: MessageEvent) => {
      if (event.origin !== pulseOrigin) {
        return;
      }
      // Mirror Pulse's internal tab/filter navigation into our own URL so
      // Back / Forward after a drill-down restores the exact Pulse state
      // the user left from (which tab, which task/grader/metric filter,
      // etc.). We use `replace: true` to avoid stacking a new history
      // entry on every intra-dashboard tab switch — only actual drill
      // navigations below (`void navigate(...)` without replace) push new
      // entries the Back button can reach.
      if (
        event.data &&
        typeof event.data === 'object' &&
        (event.data as { type?: unknown }).type === 'teamspulse:navigate'
      ) {
        const tabId = (event.data as { tabId?: unknown }).tabId;
        const params = (event.data as { params?: unknown }).params;
        if (typeof tabId !== 'string' || tabId.length === 0) return;
        // Build the inner filter-params blob first so we can decide
        // whether the resulting URL is "default" (summary + no filters)
        // and drop the host-owned keys entirely in that case.
        //
        // Pulse's tab strip emits `{ type, tabId }` without a `params`
        // key while keeping its current top-level filter values. Treating
        // an absent `params` as "clear all filters" would blow away the
        // mirror the user drilled from, so we detect that case here and
        // leave the existing `_pulseParams` untouched. An explicit empty
        // object (or explicit filter blob) is the only signal to rewrite.
        const paramsProvided =
          params !== undefined &&
          params !== null &&
          typeof params === 'object' &&
          !Array.isArray(params);
        let innerEncoded = '';
        if (paramsProvided) {
          const inner = new URLSearchParams();
          for (const [k, v] of Object.entries(
            params as Record<string, unknown>,
          )) {
            // Skip host-owned keys that the src builder overrides
            // anyway, so we don't bloat the URL with duplicates.
            if (k === 'tab' || k === 'lockFilters') continue;
            if (k === config.teamsPulseWorkspaceParam) continue;
            if (typeof v === 'string' && v.length > 0) inner.set(k, v);
          }
          innerEncoded = inner.toString();
        }
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            const existingParams = next.get('_pulseParams') ?? '';
            const effectiveParams = paramsProvided
              ? innerEncoded
              : existingParams;
            const isDefaultView = tabId === 'summary' && effectiveParams === '';
            if (isDefaultView) {
              // Summary + no filters is the URL the tab renders on a cold
              // load, so keep the address bar clean: drop both host-owned
              // keys instead of writing `?_pulseTab=summary`.
              next.delete('_pulseTab');
              next.delete('_pulseParams');
            } else {
              next.set('_pulseTab', tabId);
              if (paramsProvided) {
                if (innerEncoded) next.set('_pulseParams', innerEncoded);
                else next.delete('_pulseParams');
              }
              // else: leave `_pulseParams` untouched — the tab-strip
              // notification didn't carry filter state.
            }
            return next;
          },
          { replace: true },
        );
        return;
      }
      if (!isAistudioNavigatePayload(event.data)) {
        return;
      }
      // Cross-workspace drill-down isn't supported (see JSDoc on the
      // payload type). Always route into the user's currently active
      // workspace; if there isn't one, drop the drill.
      const targetWorkspace = activeName;
      if (!targetWorkspace) {
        return;
      }
      // Purge any legacy sticky-task cache from earlier iterations of this
      // handler. The dashboard filter's `taskName` is the ADX TaskName,
      // which the AI Studio /eval-runs API can't match against (it filters
      // by `displayName`), so caching that value would produce a stale
      // `?search=` on every subsequent drill.
      try {
        sessionStorage.removeItem(STICKY_TASK_STORAGE_KEY(targetWorkspace));
      } catch {
        // Ignore: private-mode / quota / storage-disabled origins.
      }
      const ws = encodeURIComponent(targetWorkspace);
      if (event.data.target === 'evalRunReport' && event.data.runId) {
        void navigate(
          `/workspaces/${ws}/evaluation/evaluation-runs/${encodeURIComponent(
            event.data.runId,
          )}/report`,
        );
        return;
      }
      if (event.data.target === 'evalRunsList') {
        const path = `/workspaces/${ws}/eval-runs`;
        const query = new URLSearchParams();
        if (event.data.taskName) {
          // AI Studio's eval-runs entity has no first-class task field, so
          // we feed the task name into the existing server-side search
          // (which matches on `displayName`). The Pulse "Tasks and agents"
          // tile row-click passes DisplayName as the `taskName` navigate
          // param — see the `r[0]||r[1]` fallback in the tile's inline JS —
          // so this string already matches `RunEntity.displayName`.
          query.set('search', event.data.taskName);
        }
        if (event.data.runDate && isValidYyyyMmDd(event.data.runDate)) {
          query.set('date', event.data.runDate);
        }
        const qs = query.toString();
        void navigate(qs ? `${path}?${qs}` : path);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [pulseOrigin, activeName, navigate, setSearchParams]);

  return (
    <div className={styles.root}>
      <iframe
        key={src}
        className={styles.frame}
        src={src}
        title={IFRAME_TITLE}
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
        allow="clipboard-read; clipboard-write"
      />
    </div>
  );
};
